import { Tooltip } from '@mui/material';
import {
  CellClassParams,
  ColDef,
  EditableCallbackParams,
  GetQuickFilterTextParams,
  IRowNode,
  ValueFormatterParams,
} from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { FieldCellEditor } from '../../../components/AgTable/cell-editors/FieldCellEditor';
import { isGroupColumn } from '../../../components/AgTable/cell-renderers/GenericIdRenderer';
import { CompanyLogoAndName } from '../../../components/grid-renderers/CompanyCellRenderer';
import {
  CompanyType,
  createCompanyDataModel,
  ICompanyDataModel,
} from '../../../data-models/company.data-model';
import { RendererType } from '../../../data-models/field-definition';
import { InvestmentType, investmentTypeToLabel } from '../../../schemas/FundReserves.schema';
import { portcosByNameMapAtomJ } from '../../../services/state/PortCosStateJ';
import { editableCellClass } from '../../../theme/gridStyle';
import { AgFormatterService } from '../../../util/ag-formatter-service';
import { withOverrides } from '../../../util/ag-grid-utils';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { createFormField } from '../../../view-models/form.view-model';
import { numericCellClasses } from '../../Finance2/common-grid-defs/commonColDefs';
import { selectedFundInvestmentsByCompanyNameState } from '../state/FPState';
import { InvestmentActionsRenderer } from './InvestmentActionsRenderer';
import { ReservesDraftDateCellEditor } from './ReservesDraftDateCellEditor';
import { isReservesDraftRow, ReservesRowData, ReservesRowDataSchema } from './reservesGridData';

interface IReservesColDefsOptions {
  suppressKeyboardEvent?: ColDef<ReservesRowData>['suppressKeyboardEvent'];
}

const editableForDraftRows = (params: EditableCallbackParams<ReservesRowData>) =>
  isReservesDraftRow(params.data);

const draftCellClassRules = {
  [editableCellClass]: (params: CellClassParams<ReservesRowData>) => isReservesDraftRow(params.data),
};

export function useReservesColDefs(options?: IReservesColDefsOptions) {
  const schemaToGrid = useSchemaToGrid();
  const { suppressKeyboardEvent } = options ?? {};

  return useMemo((): ColDef<ReservesRowData>[] => {
    const schema = ReservesRowDataSchema();

    const overrides: Partial<Record<keyof ReservesRowData, ColDef<ReservesRowData>>> = {
      company: {
        colId: 'company.name',
        field: 'company.name',
        initialHide: true,
        cellRendererParams: {
          displayLogoInGroupRow: true,
        },
        sortable: true,
        initialSort: 'asc',
        getQuickFilterText: (params: GetQuickFilterTextParams<ReservesRowData>) => {
          return `${params.data?.company?.name ?? ''} ${params.data?.company?.website ?? ''}`;
        },
        comparator: (a: string, b: string, nodeA: IRowNode, nodeB: IRowNode) => {
          const companyTypeA = nodeA?.data?.company?.companyType;
          const companyTypeB = nodeB?.data?.company?.companyType;
          if (companyTypeA == companyTypeB) {
            return a?.localeCompare(b) ?? 0;
          } else {
            return companyTypeA === CompanyType.portfolio
              ? 1
              : companyTypeB === CompanyType.portfolio
                ? -1
                : 0;
          }
        },
        enableRowGroup: true,
        initialRowGroup: true,
        rowGroupIndex: 0,
        filter: true,
      },
      description: {
        spanRows: true,
        tooltipField: 'description',
      },
      projectedReserves: {
        filter: false,
        cellClass: numericCellClasses,
      },
      reservesRemaining: {
        filter: false,
        cellClass: numericCellClasses,
        cellClassRules: {
          ['negative']: (params: CellClassParams<ReservesRowData>) => params.value < 0,
        },
      },
      sectorId: {
        cellClass: '',
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: AgFormatterService.get().getFormatterForId('sector'),
        },
        rowGroup: true,
        spanRows: true,
      },
      type: {
        valueFormatter: (params: ValueFormatterParams<ReservesRowData>) =>
          params.value ? investmentTypeToLabel[params.value as InvestmentType] : '',
        sortable: true,
        initialSort: 'desc',
        sortIndex: 2,
        width: 180,
      },
      date: {
        sortable: true,
        width: 180,
        initialSort: 'asc',
        sortIndex: 3,
        editable: editableForDraftRows,
        cellEditor: ReservesDraftDateCellEditor,
        cellEditorParams: {
          formField: createFormField({
            key: 'date',
            renderer: RendererType.date,
            autoFocus: true,
            fullWidth: true,
          }),
        },
        cellDataType: false,
        cellClassRules: draftCellClassRules,
        suppressKeyboardEvent,
      },
      investmentAmount: {
        cellClass: numericCellClasses,
        editable: editableForDraftRows,
        cellEditor: FieldCellEditor,
        cellEditorParams: {
          formField: createFormField({
            key: 'investmentAmount',
            renderer: RendererType.number,
            autoFocus: true,
            fullWidth: true,
          }),
        },
        cellClassRules: draftCellClassRules,
        suppressKeyboardEvent,
      },
      transactionGroupName: {
        filter: true,
        enableRowGroup: true,
        comparator: (a: string, b: string) => {
          if (a === b) {
            return 0;
          }
          if (a?.toLowerCase() === 'hypothetical') {
            return -1;
          }
          if (b?.toLowerCase() === 'hypothetical') {
            return 1;
          }
          return typeof a === 'string' && typeof b === 'string'
            ? a.toLowerCase().localeCompare(b.toLowerCase())
            : 0;
        },
      },
    };

    const colDefs = schemaToGrid(schema, [
      'company',
      'description',
      'sectorId',
      'transactionGroupName',
      'type',
      'date',
      'investmentAmount',
      'initialInvestment',
      'reservesAssigned',
      'reservesInvested',
      'reservesRemaining',
      'projectedReserves',
    ]);

    const menuColumn: ColDef = {
      filter: false,
      maxWidth: 132,
      pinned: 'right',
      cellRenderer: InvestmentActionsRenderer,
      suppressHeaderMenuButton: true,
      headerName: 'Actions',
      headerStyle: {
        color: 'transparent',
      },
    };
    colDefs.push(menuColumn);
    return withOverrides(colDefs, overrides) as ColDef[];
  }, [schemaToGrid, suppressKeyboardEvent]);
}

export const defaultReservesColDefs: ColDef<ReservesRowData> = {
  enableValue: true,
  flex: 1,
  minWidth: 200,
};

export function getReservesAutoGroupDef(): ColDef {
  return {
    getQuickFilterText: () => '',
    filter: 'agGroupColumnFilter',
    sortable: true,
    width: 250,
    initialSort: 'asc',
    sortIndex: 1,
  };
}
export interface FundCompanyModel extends Partial<ICompanyDataModel> {
  name: string;
  description?: string | null;
  sectorId?: number | null;
}
export interface IFundCompanyCellRendererProps<
  T extends { company: FundCompanyModel },
> extends CustomCellRendererProps<T> {
  displayLogoInGroupRow?: boolean;
}
export function FundCompanyCellRenderer<
  T extends { company: FundCompanyModel; description?: string | null; sectorId?: number | null },
>(params: IFundCompanyCellRendererProps<T>) {
  const { data, node, value, displayLogoInGroupRow } = params;
  const portcosByName = useAtomValue(portcosByNameMapAtomJ);
  const fundInvestmentsByCompanyName = useAtomValue(selectedFundInvestmentsByCompanyNameState);
  let company = data?.company;
  const name = value || node.key || 'Unknown Company';
  const investment = fundInvestmentsByCompanyName.get(name);
  if (!company) {
    company =
      portcosByName.get(node.key ?? '') ??
      createCompanyDataModel({
        id: -1,
        name,
        description: investment?.description ?? '',
        sectorId: investment?.sectorId ?? null,
      });
  }

  if (!displayLogoInGroupRow && isGroupColumn(params)) {
    return <>{name}</>;
  }

  if (!isGroupColumn(params) && node.group) {
    return <></>;
  }

  return (
    <Tooltip enterDelay={500} title={company.description ?? ''}>
      <div>
        <CompanyLogoAndName
          name={company.name}
          logoUrl={company.logoUrl ?? ''}
          className={company.companyType === CompanyType.portfolio ? undefined : 'hypothetical'}
        />
      </div>
    </Tooltip>
  );
}
